<template>
  <BHeightAdjuster>
    <BModalBody>
      <div class="setting-message">
        {{ $t('callTarget.setting.message') }}
      </div>
      <div
        id="list-content"
        class="list-content"
      >
        <div v-loading="loadingItems">
          <div class="item-content">
            <BEmptyBox
              v-if="!$wait.is('getOpportunityCustomFieldsWait') && sortedOpportunityCustomFields.length === 0"
              class="py-400"
              display-only
            >
              <slot name="text">
                {{ $t('opportunityCustomField.empty') }}
              </slot>
            </BEmptyBox>
            <Draggable
              v-model="sortedOpportunityCustomFields"
              class="item-row"
              item-key="id"
              handle=".handle"
              :animation="300"
              @end="updateOpportunityCustomFields"
            >
              <template #item="{ element: field }">
                <div>
                  <BLayout
                    class="py-100"
                    align-center
                    justify-space-between
                  >
                    <BLayout
                      class="mr-100"
                      align-center
                    >
                      <BIcon
                        class="handle"
                        size="small"
                        type="gray"
                      >
                        drag_indicator
                      </BIcon>
                      <BTooltip
                        top
                        :content="$t(`opportunityCustomField.dataType.enum.${convertToCamelcaseKeys(field.dataType)}`)"
                      >
                        <BDataTypeIcon
                          :data-type="field.dataType"
                        />
                      </BTooltip>
                      <BEditableContent
                        :value="field.name"
                        :placeholder="$t('opportunityCustomField.name')"
                        required
                        :loading="updateLoading"
                        @check="handleUpdateName($event, field.id)"
                      />
                      <BEditableContent
                        class="system-reference-name-input"
                        :value="field.systemReferenceName"
                        :placeholder="$t('opportunityCustomField.systemReferenceName')"
                        required
                        :loading="updateLoading"
                        @check="handleUpdateSystemReferenceKey($event, field.id)"
                      />
                    </BLayout>
                    <BMoreMenu>
                      <BPopover
                        v-if="field.deleteDisable"
                        placement="top"
                        width="240"
                        trigger="hover"
                      >
                        <template #reference>
                          <div>
                            <BBtn
                              flat
                              fit
                              size="small"
                              disabled
                            >
                              {{ $t('opportunityCustomField.delete') }}
                            </BBtn>
                          </div>
                        </template>
                        <div>{{ $t('opportunityCustomField.validation.usedMappingBySalesforce') }}</div>
                      </BPopover>
                      <BBtn
                        v-else
                        flat
                        fit
                        size="small"
                        @click="() => deleteOpportunityCustomField(field)"
                      >
                        {{ $t('opportunityCustomField.delete') }}
                      </BBtn>
                    </BMoreMenu>
                  </BLayout>
                  <OpportunityCustomFieldOption
                    v-if="field.options"
                    :opportunity-custom-field-option="field.options"
                    :opportunity-custom-field-id="field.id"
                    :opportunity-custom-field-name="field.name"
                    :loading="updateLoading"
                    @delete="handleDeleteOpportunityCustomFieldOption"
                  />
                </div>
              </template>
            </Draggable>
          </div>
        </div>
      </div>
    </BModalBody>
    <BModalFooter>
      <OpportunityCustomFieldCreatingForm class="footer" />
    </BModalFooter>
  </BHeightAdjuster>
</template>

<script>
import lodash from 'lodash';
import { mapWaitingActions } from 'vue-wait';
import Draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import OpportunityCustomFieldCreatingForm from '@/components/organisms/user/setting/item/OpportunityCustomFieldCreatingForm.vue';
import OpportunityCustomFieldOption from '@/components/organisms/user/setting/item/OpportunityCustomFieldOption.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useErrorHandler } from '@/composable/error-handler';

export default {
  components: {
    Draggable,
    OpportunityCustomFieldOption,
    OpportunityCustomFieldCreatingForm,
  },
  setup() {
    const { isAvailableSalesforceLinkFeature } = useAvailableFeatureCheck();
    const { defaultHandler } = useErrorHandler();
    return {
      isAvailableSalesforceLinkFeature,
      defaultHandler,
    };
  },
  data () {
    return {
      sortedOpportunityCustomFields: [],
    };
  },
  computed: {
    ...mapGetters('user', [
      'opportunityCustomFields',
    ]),
    createLoading() {
      return this.$wait.is(['createOpportunityCustomFieldWait', 'getOpportunityCustomFieldsWait']);
    },
    updateLoading() {
      return this.$wait.is(['updateOpportunityCustomFieldWait', 'getOpportunityCustomFieldsWait']);
    },
    loadingItems() {
      return this.$wait.is(['getOpportunityCustomFieldsWait', 'deleteOpportunityCustomFieldWait', 'deleteOpportunityCustomFieldOptionWait']);
    },
  },
  watch: {
    opportunityCustomFields (newValue) {
      this.sortedOpportunityCustomFields = this.sortOrder(lodash.cloneDeep(newValue));
    },
  },
  created () {
    this.getOpportunityCustomFieldsAction();
  },
  methods: {
    ...mapWaitingActions('user', {
      getOpportunityCustomFieldsAction: 'getOpportunityCustomFieldsWait',
      updateOpportunityCustomFieldAction: 'updateOpportunityCustomFieldWait',
      updateOpportunityCustomFieldsAction: 'updateOpportunityCustomFieldsWait',
      deleteOpportunityCustomFieldAction: 'deleteOpportunityCustomFieldWait',
    }),
    sortOrder (array) {
      if (!array) { return null; }

      return array.slice().sort(function (a, b) {
        return a.sortOrder > b.sortOrder;
      });
    },
    async handleUpdateName (newFieldName, opportunityCustomFieldId) {
      await this.updateOpportunityCustomFieldAction({
        opportunityCustomFieldId,
        body: { opportunityCustomField: { name: newFieldName } },
        errorHandlers: {
          422: this.defaultHandler,
        },
      });
    },
    async handleUpdateSystemReferenceKey (newValue, opportunityCustomFieldId) {
      const ok = await this.$bitterAlert.show({
        title: this.$t('opportunityCustomField.title'),
        text: this.$t('opportunityCustomField.systemReferenceNameChangingNotice'),
      });
      if (!ok) { return; }
      await this.updateOpportunityCustomFieldAction({
        opportunityCustomFieldId,
        body: { opportunityCustomField: { systemReferenceName: newValue } },
        errorHandlers: {
          422: this.defaultHandler,
        },
      });
    },
    async updateOpportunityCustomFields () {
      const opportunityCustomFields = this.sortedOpportunityCustomFields.map((opportunityCustomField, index) => {
        opportunityCustomField.sortOrder = index;
        return opportunityCustomField;
      });
      await this.updateOpportunityCustomFieldsAction({
        body: { opportunityCustomFields },
        errorHandlers: {
          422: this.defaultHandler,
        },
      });
    },
    async deleteOpportunityCustomField (field) {
      const check = await this.$bitterAlert.show({
        title: this.$t('opportunityCustomField.title'),
        text: `${this.$t('general.confirm.actionWithTarget', { target: field.name, action: this.$t('general.delete.text') })}\n\n${this.$t('opportunityCustomField.deleteMessage')}`,
      });
      if (!check) { return; }
      await this.deleteOpportunityCustomFieldAction({
        opportunityCustomFieldId: field.id,
      });
      await this.refresh();
    },
    async handleDeleteOpportunityCustomFieldOption() {
      await this.refresh();
    },
    async refresh() {
      await this.getOpportunityCustomFieldsAction();
      const listContent = document.getElementById('list-content');
      listContent.scrollTo({ top: listContent.scrollHeight, left: 0, behavior: 'smooth' });
    },
    convertToCamelcaseKeys (dataType) {
      if (dataType.indexOf('_') == -1) {
        return dataType;
      }
      return dataType.split('_').map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }).join('');
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.b-modal-body) {
  margin-top: 20px;
  padding: 0 32px 20px;
  .list-content {
    overflow-y: auto;
    .item-content {
      height: calc(100vh - 320px);
    }
    .item-row {
      margin: 0;
      @media only screen and (min-width: 959px) {
        width: 50%;
      }
      .b-input-box {
        margin-left: 8px;
      }
    }
  }
}

.list-content {
  .system-reference-name-input {
    width: 280px;
    word-break: break-all
  }
}

.setting-message {
  color: $textcolor-light;
  margin-bottom: $basespace-400;
}

.handle {
  cursor: grab;
  margin-right: $basespace-50;
}

.form-box {
  background: $bgcolor-base;
  padding: $basespace-200;
}

.sortable-chosen {
  opacity: 0.5;
  background-color: $bgcolor-base;
}
.sortable-ghost {
  background-color: $bgcolor-dark;
}

.footer {
  padding-top: $basespace-300;
}
</style>
